import { scrubPosthogEvent } from './telemetryUrlScrub';
import { runAfterIdle } from './idle';

type PostHogClient = typeof import('posthog-js').posthog;

const posthogApiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const posthogApiHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

const disablePosthogInEU =
  String(import.meta.env.VITE_DISABLE_POSTHOG_IN_EU).toLowerCase() === 'true';

let posthogPromise: Promise<PostHogClient | null> | null = null;
let countryPromise: Promise<string | null> | null = null;
let shouldEnablePosthogPromise: Promise<boolean> | null = null;

const EUROPEAN_COUNTRY_CODES = new Set([
  'AD',
  'AL',
  'AM',
  'AT',
  'AX',
  'AZ',
  'BA',
  'BE',
  'BG',
  'BY',
  'CH',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FO',
  'FR',
  'GB',
  'GE',
  'GG',
  'GI',
  'GR',
  'HR',
  'HU',
  'IE',
  'IM',
  'IS',
  'IT',
  'JE',
  'KZ',
  'LI',
  'LT',
  'LU',
  'LV',
  'MC',
  'MD',
  'ME',
  'MK',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'RS',
  'RU',
  'SE',
  'SI',
  'SJ',
  'SK',
  'SM',
  'TR',
  'UA',
  'VA',
  'XK',
]);

const fetchCountryFromApi = async (): Promise<string | null> => {
  if (!countryPromise) {
    countryPromise = fetch('/api/country')
      .then(async (res) => {
        if (!res.ok) return null;
        const data = await res.json();
        if (data && typeof data.country === 'string') {
          return data.country.trim().toUpperCase();
        }
        return null;
      })
      .catch(() => null);
  }

  return countryPromise;
};

const isEuropeanTimezone = (): boolean => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return timezone.startsWith('Europe/');
  } catch {
    return false;
  }
};

export const shouldEnablePosthog = async (): Promise<boolean> => {
  if (!disablePosthogInEU) {
    return true;
  }

  if (!shouldEnablePosthogPromise) {
    shouldEnablePosthogPromise = (async () => {
      const countryCode = await fetchCountryFromApi();
      if (countryCode) {
        return !EUROPEAN_COUNTRY_CODES.has(countryCode);
      }

      return !isEuropeanTimezone();
    })();
  }

  return shouldEnablePosthogPromise;
};

const loadPosthog = (): Promise<PostHogClient | null> => {
  if (!posthogPromise) {
    posthogPromise = import('posthog-js')
      .then((module) => {
        const posthogModule = module as
          typeof import('posthog-js') | null | undefined;

        return posthogModule?.posthog ?? posthogModule?.default ?? null;
      })
      .catch(() => null)
      .then((posthog) => {
        if (!posthog) {
          posthogPromise = null;
        }

        return posthog;
      });
  }

  return posthogPromise;
};

export const getPosthog = async () => {
  const posthog = await loadPosthog();

  if (
    posthog &&
    process.env.NODE_ENV === 'production' &&
    typeof window !== 'undefined' &&
    posthogApiKey &&
    !posthog.__loaded
  ) {
    posthog.init(posthogApiKey, {
      api_host: posthogApiHost,
      autocapture: false,
      disable_session_recording: true,
      persistence: 'cookie',
      // Lightweight Core Web Vitals reporting (LCP, FCP, CLS, INP) through the
      // analytics stack we already load. This is the only source of field INP —
      // the Sentry browser tracing integration does not report it.
      capture_performance: { web_vitals: true },
      // One-time secrets in URLs (reset, verify, sign-in tokens) never
      // reach analytics (ORA-1089).
      before_send: scrubPosthogEvent,
    });
  }

  return posthog;
};

export const withPosthog = (
  callback: (posthog: PostHogClient) => void
): void => {
  if (process.env.NODE_ENV !== 'production' || typeof window === 'undefined') {
    return;
  }

  void shouldEnablePosthog().then((enabled) => {
    if (!enabled) return;

    void getPosthog().then((posthog) => {
      if (posthog) callback(posthog);
    });
  });
};

export const withPosthogAfterIdle = (
  callback: (posthog: PostHogClient) => void,
  timeout = 2000
) => runAfterIdle(() => withPosthog(callback), timeout);

export const resetPosthog = () => {
  withPosthog((posthog) => {
    posthog.reset();
  });
};
